import _ from 'lodash';
import Combinatorics from 'js-combinatorics';

import GameData from 'shared/gameData';

export const Covenants = GameData.Covenants;
export const Conduits = GameData.Conduits;
export const ConduitRanks = GameData.ConduitRanks;

export const Soulbinds = _.flatten(_.map(Covenants, 'soulbinds'));

export const getCovenant = idOrKey => {
  const covenant = _.find(Covenants, cov => cov.id === idOrKey || cov.key === idOrKey);
  if (!covenant) {
    return null;
  }
  return _.omit(covenant, ['soulbinds']);
};

export const getCovenantSoulbinds = idOrKey => {
  const covenant = getCovenant(idOrKey);
  return _.filter(Soulbinds, { covenantId: covenant.id });
};

// TODO: This needs to check other localized names
export const getSoulbind = idOrKey => {
  const soulbind = _.find(Soulbinds, s => s.id === idOrKey || s.key === idOrKey);
  return soulbind;
};

export const getConduit = (id, rank) => {
  const conduit = _.find(Conduits, { id: parseInt(id) });
  if (conduit) {
    const conduitRank = _.find(conduit.ranks, { rank: parseInt(rank, 10) });
    if (conduitRank) {
      const rankInfo = _.find(ConduitRanks, { rank: parseInt(rank, 10) });
      return {
        ...conduit,
        rank: conduitRank.rank,
        spellId: conduitRank.spellId,
        icon: conduitRank.icon,
        itemLevel: rankInfo.itemLevel,
      };
    }
  }

  return null;
};

// this depends on the SimC soulbinds string being sorted by row
export const parseSoulbindSimcString = string => {
  let result = {};

  const [soulbindNameStr, soulbindTalentsStr] = _.split(string, ',');
  const [soulbindName, soulbindId] = _.split(soulbindNameStr, ':');

  const soulbind = getSoulbind(parseInt(soulbindId, 10) || soulbindName);
  if (!soulbind) {
    return null;
  }
  result = _.omit(soulbind, ['talents']);

  const talentsSplit = _.split(soulbindTalentsStr, '/');
  // keep track of how many conduits have been placed so we can find the right slot in the loop
  const conduitsPlaced = {};
  let tier = 0;
  const talents = _.map(talentsSplit, (talentStr) => {
    const [idStr, rankStr] = _.split(talentStr, ':');
    const id = parseInt(idStr, 10);
    const rank = parseInt(rankStr, 10);
    if (rank) {
      // conduit
      const conduit = getConduit(id, rank);

      if (conduit) {
        if (!conduitsPlaced[conduit.type]) {
          conduitsPlaced[conduit.type] = 0;
        }
        // figure out where this conduit is slotted
        const possibleSlots = _.filter(soulbind.talents, t => {
          return t.conduitType === conduit.type && t.tier > tier;
        });
        const soulbindSlot = _.first(possibleSlots);
        conduitsPlaced[conduit.type] += 1;

        tier = soulbindSlot?.tier || tier;

        // merge slot and conduit info
        // this gets name/icon for conduit while retaining some data about position in the tree
        return {
          ...soulbindSlot,
          ...conduit,
        };
      }

      return null;
    }

    // spell
    if (soulbind) {
      const talent = _.find(soulbind.talents, { spellId: id });
      tier = talent?.tier || tier;
      return talent;
    }

    return null;
  });
  result.talents = _.filter(talents);

  return result;
};

export const stringifySoulbind = (soulbind) => {
  if (!soulbind) {
    return null;
  }
  const key = `${soulbind.key}:${soulbind.id}`;
  // TODO: Sort conduits into a deterministic order for easier comparisons later?
  const talents = _.join(
    _.map(soulbind.talents, talent => {
      if (talent) {
        if (talent.conduitType || talent.type !== 'spell') {
          return [talent.id, talent.rank].join(':');
        }
        return talent.spellId;
      }
      return null;
    }),
    '/',
  );

  return [key, talents].join(',');
};

export const legendaryStatBonusIds = {
  crit: 6647,
  haste: 6649,
  mastery: 6648,
  vers: 6650,
};

export const legendaryStatCombos = Combinatorics.combination(_.keys(legendaryStatBonusIds), 2).toArray();

export const legendaryLevels = [
  190,
  210,
  225,
  235,
];

export const legendaryItemLimitCategory = 473;

export const getLegendaryAbilities = (specIds, slot) => {
  const specArray = !_.isArray(specIds) ? [specIds] : specIds;
  return _.filter(GameData.LegendaryAbilities, ability => {
    return _.size(_.intersection(ability.specUsable, specArray)) > 0 && _.includes(ability.slots, slot);
  });
};

